/**
 * Word-anywhere search over imported records, on a database that has no substring search.
 *
 * Firestore can only match a field exactly, by range, or by array membership. A range over an
 * ordered field gives you PREFIX matching, which is what the picker had: "smi" found
 * `SMITH HOLDINGS` only if the name began with it, so searching "smith" for
 * `JOHN SMITH HOLDINGS` found nothing and the record looked absent. There is no query that
 * finds a substring, at any price.
 *
 * So the matching is moved into the data. At import time each record gets a `searchTokens`
 * array holding every WORD PREFIX of every searchable column, and a search becomes
 * `array-contains` — an exact match on one token, which Firestore does index. "smith" is a
 * token of `JOHN SMITH HOLDINGS` because the words are tokenized separately, and "smi" is a
 * token because prefixes are included, so type-ahead still works mid-word.
 *
 * WHAT THIS IS NOT: it matches from the start of a WORD, not from anywhere inside one. "mith"
 * does not find `SMITH`. True substring matching means storing every substring of every value —
 * roughly two hundred tokens per short name rather than a handful — and that is a real cost in
 * index entries and write time for a case ("I remember the middle of a word but not the start")
 * that barely happens. Said out loud in the UI rather than left to be discovered.
 *
 * WHICH columns are searchable is configuration: any column an administrator marks. The record
 * number and name always are, since a directory whose identifier is not searchable is useless.
 *
 * Faithful port of functions/src/feeds/searchTokens.js — functions/ deploys standalone and
 * cannot import from web/. The QUERY side has to tokenize what the user typed exactly the way the
 * import tokenized the data, or a term stored as a token gets looked up in a different shape and
 * matches nothing. Keep them in sync; a test asserts they agree.
 */

/**
 * Longest prefix stored, and therefore the longest term that can be looked up.
 *
 * Every character of every word costs one more array element, and array elements are index
 * entries: a 12-character cap keeps a typical record near a hundred tokens instead of several
 * hundred. Queries truncate to the same length, so a longer search term still matches on its
 * first twelve characters rather than silently finding nothing.
 */
export const MAX_TOKEN_LENGTH = 12;

/** Shortest token stored. One-character tokens match most of the directory and help nobody. */
export const MIN_TOKEN_LENGTH = 2;

/** A ceiling for pathological rows, so one absurd value cannot bloat a document. */
export const MAX_TOKENS_PER_RECORD = 300;

/** Lower-case words, punctuation and accents flattened, so `D'ARRIGO BROS.` is two words. */
export function words(value) {
  return String(value == null ? '' : value)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * The prefixes of one word, plus the word itself.
 *
 * A leading-zero variant is added for identifiers: client numbers arrive as `005747` and people
 * type `5747`, which is not a prefix of it and would otherwise never match.
 */
function prefixesOf(word) {
  const out = new Set();
  const add = (text) => {
    const limit = Math.min(text.length, MAX_TOKEN_LENGTH);
    for (let length = MIN_TOKEN_LENGTH; length <= limit; length += 1) out.add(text.slice(0, length));
    if (text.length === 1) out.add(text);
  };
  add(word);
  const unpadded = word.replace(/^0+/, '');
  if (unpadded && unpadded !== word) add(unpadded);
  return out;
}

/**
 * Which columns feed the search. Number and name always do; anything else opts in with
 * `searchable: true`, and an ignored column never does however it is flagged.
 */
export function searchableColumns(columns) {
  return (Array.isArray(columns) ? columns : []).filter((column) => {
    if (!column || !column.key || column.role === 'ignore') return false;
    if (column.role === 'number' || column.role === 'name') return true;
    return column.searchable === true;
  });
}

/**
 * @param {{clientNumber: string, clientName: string, data: Object}} record  a mapped record
 * @param {Array<Object>} columns  the feed's column mapping
 * @returns {string[]} tokens to store on the document, capped and de-duplicated
 */
export function buildSearchTokens(record, columns) {
  const tokens = new Set();
  const values = [];

  // The number and name are read from the record itself rather than from `data`, so a record
  // still gets tokens even if a caller built it without the data map.
  if (record && record.clientNumber) values.push(record.clientNumber);
  if (record && record.clientName) values.push(record.clientName);
  for (const column of searchableColumns(columns)) {
    if (column.role === 'number' || column.role === 'name') continue;
    const value = record && record.data ? record.data[column.key] : '';
    if (value) values.push(value);
  }

  for (const value of values) {
    for (const word of words(value)) {
      for (const token of prefixesOf(word)) {
        tokens.add(token);
        if (tokens.size >= MAX_TOKENS_PER_RECORD) return [...tokens];
      }
    }
  }
  return [...tokens];
}

/**
 * The terms a typed search becomes.
 *
 * Truncated to MAX_TOKEN_LENGTH because that is the longest token the import stored: without
 * this, typing a thirteen-character word would look up a token that cannot exist and find
 * nothing, which is the worst possible failure for a search box.
 */
export function queryTerms(text) {
  return words(text)
    .filter((word) => word.length >= MIN_TOKEN_LENGTH)
    .map((word) => word.slice(0, MAX_TOKEN_LENGTH));
}

/**
 * The term to hand to `array-contains`. Firestore permits only one such clause per query, so
 * the remaining terms are applied in the caller; the longest is chosen because it is the most
 * selective and therefore returns the smallest set to filter.
 */
export function mostSelectiveTerm(terms) {
  return [...terms].sort((a, b) => b.length - a.length)[0] || '';
}

/** Whether a record's stored tokens satisfy EVERY term — the AND half Firestore cannot do. */
export function matchesAllTerms(tokens, terms) {
  const held = new Set(Array.isArray(tokens) ? tokens : []);
  return terms.every((term) => held.has(term));
}
